/**
 * Incoming-fire bearing cue (37.16).
 *
 * When an enemy shot the viewer cannot see is going to land near one of the
 * viewer's units, that unit gets an amber ring on the ground with a notch on
 * the side the shot is coming from. Direction only — never distance, never the
 * shooter's position. Pulses from launch until the flight ends.
 *
 * Rendering only. The game decides when a cue exists (from the precomputed
 * flight) and disposes it when the flight ends.
 */

import * as THREE from 'three';
import type { Vec3 } from '../types';

export interface ThreatCue {
  group: THREE.Group;
  ring: THREE.Mesh;
  notch: THREE.Mesh;
  unitId: number;
  phase: number;
}

export const THREAT_COLOR = '#ffb547';
const RING_INNER = 1.05;
const RING_OUTER = 1.3;
const PULSE_HZ = 1.4;

/**
 * A ring around the unit, lying on its ground plane (normal `up`), with a notch
 * pointing along `bearing` — the direction the shot arrives from, already in the
 * ground plane.
 */
export function createThreatCue(scene: THREE.Scene, unitId: number, pos: Vec3, up: Vec3, bearing: Vec3): ThreatCue {
  const group = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(THREAT_COLOR), transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false });

  const ring = new THREE.Mesh(new THREE.RingGeometry(RING_INNER, RING_OUTER, 40), mat);
  group.add(ring);

  // Notch: a small wedge on the ring's rim, on the bearing side
  const notchGeo = new THREE.ConeGeometry(0.28, 0.6, 3);
  const notch = new THREE.Mesh(notchGeo, mat);
  notch.position.set(RING_OUTER + 0.25, 0, 0); // along local +x; the group is oriented so +x = bearing
  notch.rotation.z = -Math.PI / 2;                // point outward
  group.add(notch);

  // Orient: local +z = up (the ring lies flat), local +x = bearing
  const upV = new THREE.Vector3(up.x, up.y, up.z).normalize();
  const bV = new THREE.Vector3(bearing.x, bearing.y, bearing.z);
  bV.addScaledVector(upV, -bV.dot(upV));
  if (bV.lengthSq() < 1e-6) bV.set(1, 0, 0).addScaledVector(upV, -upV.x);
  bV.normalize();
  const yV = new THREE.Vector3().crossVectors(upV, bV).normalize();
  const m = new THREE.Matrix4().makeBasis(bV, yV, upV);
  group.quaternion.setFromRotationMatrix(m);
  group.position.set(pos.x + up.x * 0.12, pos.y + up.y * 0.12, pos.z + up.z * 0.12);
  group.renderOrder = 7;
  scene.add(group);
  return { group, ring, notch, unitId, phase: Math.random() * Math.PI * 2 };
}

/** Pulse: the ring breathes and the notch flashes, so it reads as live. */
export function updateThreatCues(cues: ThreatCue[], time: number): void {
  for (const c of cues) {
    const t = 0.5 + 0.5 * Math.sin(time * PULSE_HZ * Math.PI * 2 + c.phase);
    const s = 1 + 0.12 * t;
    c.ring.scale.set(s, s, 1);
    (c.ring.material as THREE.MeshBasicMaterial).opacity = 0.45 + 0.45 * t;
    c.notch.scale.setScalar(1 + 0.35 * t);
  }
}

export function disposeThreatCues(scene: THREE.Scene, cues: ThreatCue[]): void {
  for (const c of cues) {
    scene.remove(c.group);
    c.ring.geometry.dispose();
    c.notch.geometry.dispose();
    (c.ring.material as THREE.Material).dispose();
  }
}

/** Direction the shot arrives from, in the unit's ground plane: the reverse of its impact velocity, flattened. */
export function arrivalBearing(finalVel: Vec3, up: Vec3): Vec3 {
  const v = new THREE.Vector3(-finalVel.x, -finalVel.y, -finalVel.z);
  const u = new THREE.Vector3(up.x, up.y, up.z).normalize();
  v.addScaledVector(u, -v.dot(u));
  if (v.lengthSq() < 1e-6) v.set(1, 0, 0).addScaledVector(u, -u.x);
  v.normalize();
  return { x: v.x, y: v.y, z: v.z };
}
